import { useState } from 'react';
import TopBar from '../components/TopBar';
import { Placeholder } from '../components/Charts';
import { PatternDialog } from '../components/triage/TriageModals';
import { kpisService } from '../lib/services';
import { useApi } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { apiErrorMessage } from '../lib/apiError';
import { formatDateTime } from '../lib/datetime';
import {
  ISSUE_TYPES, issueLabel, issuePillClass, trendColor, canGoPrev, canGoNext, pageCount, rangeLabel,
} from '../lib/triage';

const PAGE_SIZE = 20;

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function TriageCoaching({ onOpenCall }) {
  const { toast } = useToast();
  const [tag, setTag] = useState('all');
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data: library, error: libError, loading, refetch: refetchLibrary } = useApi(
    () => kpisService.libraryPatterns({ tag, page, size: PAGE_SIZE }),
    { fallback: null, deps: [tag, page] },
  );
  const { data: overview, refetch: refetchOverview } = useApi(
    () => kpisService.libraryOverview(),
    { fallback: null, deps: [] },
  );

  const patterns = library?.content || [];
  const counts = library?.counts || {};
  const total = library?.totalElements ?? 0;
  const reload = () => { refetchLibrary(); refetchOverview(); };

  const loop = [
    ['CALLS',    `${overview?.callsThisWeek ?? 0}/wk`,         'voice calls this week'],
    ['TRIAGE',   `${overview?.flaggedCalls ?? 0} flagged`,     'sentiment + manual flags'],
    ['ANNOTATE', `${overview?.annotationsThisWeek ?? 0}`,      'annotations this week'],
    ['LIBRARY',  `${overview?.patterns ?? 0} patterns`,        'reusable coaching set'],
    ['EVAL',     'Not set up',                                  'prompt regression tests'],
    ['DEPLOY',   'Not set up',                                  'staged rollout'],
    ['MEASURE',  'Not set up',                                  'quality uplift'],
  ];

  const pickTag = (t) => { setTag(t); setPage(0); };

  const onExport = async () => {
    setExporting(true);
    try {
      downloadBlob(await kpisService.libraryExport(), 'coaching-patterns.json');
      toast('Exported the coaching library', { variant: 'success' });
    } catch (err) {
      toast(`Export failed: ${apiErrorMessage(err, 'unknown error')}`, { variant: 'danger' });
    } finally {
      setExporting(false);
    }
  };

  const onAdd = async (v) => {
    setBusy(true);
    try {
      await kpisService.createPattern(v);
      toast('Added to the coaching library', { variant: 'success' });
      setDialog(false);
      reload();
    } catch (err) {
      toast(`Couldn’t add the pattern: ${apiErrorMessage(err, 'unknown error')}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (p) => {
    if (!window.confirm(`Remove “${p.title}” from the coaching library?`)) return;
    try {
      await kpisService.archivePattern(p.id);
      toast('Pattern removed', { variant: 'success' });
      reload();
    } catch (err) {
      toast(`Couldn’t remove it: ${apiErrorMessage(err, 'unknown error')}`, { variant: 'danger' });
    }
  };

  return (
    <>
      <TopBar crumbs={['Triage & QA', 'Coaching library']} right={
        <>
          <button className="btn small" disabled={exporting || (overview?.patterns ?? 0) === 0} onClick={onExport}
                  title="Download the patterns as JSON test cases">
            {exporting ? 'Exporting…' : 'Export to TDD'}
          </button>
          <button className="btn small primary" onClick={() => setDialog(true)}>+ Add pattern</button>
        </>
      } />
      <div className="app-content">
        <div className="card animate-in" style={{ marginBottom: 16 }}>
          <div className="card-label">THE LOOP — TRIAGE → CORRECT → DEPLOY → MEASURE</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 0' }}>
            {loop.map(([t, v, sub], i, arr) => (
              <div key={t} style={{ display: 'contents' }}>
                <div style={{ flex: 1, textAlign: 'center', padding: 10, background: 'var(--bg-glass)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                  <div className="mono text-xs text-muted">{t}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, opacity: v === 'Not set up' ? 0.5 : 1 }}>{v}</div>
                  <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{sub}</div>
                </div>
                {i < arr.length - 1 && <span style={{ color: 'var(--accent-primary)', fontSize: 18 }}>→</span>}
              </div>
            ))}
          </div>
          <div className="mono text-xs text-muted">
            Evaluating prompts and rolling them out in stages are not set up yet; the first four steps are live.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }}>
          <div className="card animate-in" style={{ animationDelay: '0.1s' }}>
            <div className="card-label">COACHING LIBRARY · {counts.all ?? 0} PATTERN{(counts.all ?? 0) === 1 ? '' : 'S'}</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              {[{ id: 'all', label: 'All' }, ...ISSUE_TYPES].map((t) => (
                <span
                  key={t.id}
                  className={`pill ${tag === t.id ? 'active' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => pickTag(t.id)}
                >
                  {t.label}{counts[t.id] != null ? ` (${counts[t.id]})` : ''}
                </span>
              ))}
            </div>

            {libError && !library && (
              <div style={{ color: 'var(--danger)', marginBottom: 8 }}>
                Couldn’t load the library: {apiErrorMessage(libError, 'unknown error')}{' '}
                <button className="btn small" onClick={reload}>Retry</button>
              </div>
            )}
            {!loading && patterns.length === 0 && !libError && (
              <div className="text-muted" style={{ padding: '24px 8px', textAlign: 'center' }}>
                {tag === 'all'
                  ? 'No patterns yet. Open a call in the Call Queue and choose “Add to coaching library”, or use “+ Add pattern”.'
                  : `No ${issueLabel(tag).toLowerCase()} patterns yet.`}
              </div>
            )}

            {patterns.map((p) => (
              <div key={p.id} className="card" style={{ marginBottom: 10, padding: 12 }}>
                <div className="flex-row" style={{ marginBottom: 8 }}>
                  <span className={`pill ${issuePillClass(p.tag)}`}>{issueLabel(p.tag)}</span>
                  <span className="mono text-xs text-muted">
                    {p.code}
                    {p.sourceCallId && (
                      <>
                        {' · from call '}
                        <a
                          href="#open-call"
                          style={{ color: 'var(--info)' }}
                          onClick={(e) => { e.preventDefault(); onOpenCall?.(p.sourceCallId); }}
                        >{String(p.sourceCallId).slice(0, 8)}</a>
                      </>
                    )}
                  </span>
                  <span style={{ marginLeft: 'auto' }} className="mono text-xs text-muted">
                    {p.createdBy ? `${p.createdBy} · ` : ''}{formatDateTime(p.createdAt, '')}
                  </span>
                  <button type="button" className="btn small" title="Remove from the library" onClick={() => onArchive(p)}>✕</button>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{p.title}</div>
                <div className="grid-2">
                  {p.badText && (
                    <div style={{ padding: 10, borderRadius: 'var(--radius-sm)', background: 'var(--danger-bg)', border: '1px solid rgba(239,68,68,0.15)' }}>
                      <div className="mono text-xs text-danger" style={{ marginBottom: 4 }}>❌ AI SAID</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{p.badText}</div>
                    </div>
                  )}
                  {p.goodText && (
                    <div style={{ padding: 10, borderRadius: 'var(--radius-sm)', background: 'var(--success-bg)', border: '1px solid rgba(16,185,129,0.15)' }}>
                      <div className="mono text-xs text-success" style={{ marginBottom: 4 }}>✓ SHOULD HAVE SAID</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{p.goodText}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {total > 0 && (
              <div className="flex-between" style={{ marginTop: 8 }}>
                <span className="mono text-xs text-muted">
                  {rangeLabel({ page, size: PAGE_SIZE, total, shown: patterns.length }, 'patterns')}
                </span>
                <div className="flex-row">
                  <span className="mono text-xs text-muted" style={{ marginRight: 8 }}>
                    Page {page + 1} of {pageCount(total, PAGE_SIZE)}
                  </span>
                  <button className="btn small" disabled={!canGoPrev(page) || loading} onClick={() => setPage(page - 1)}>Prev</button>
                  <button className="btn small" disabled={!canGoNext(page, total, PAGE_SIZE) || loading} onClick={() => setPage(page + 1)}>Next</button>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="card animate-in" style={{ animationDelay: '0.15s' }}>
              <div className="card-label">WEEKLY TREND BY ISSUE TYPE</div>
              {(overview?.trends || []).length === 0 ? (
                <div className="text-muted text-xs">No annotations in the last two weeks.</div>
              ) : (
                <>
                  <div className="mono text-xs text-muted" style={{ marginBottom: 6 }}>previous 7 days → last 7 days</div>
                  {overview.trends.map((t) => (
                    <div key={t.tag} className="flex-row" style={{ marginBottom: 8 }}>
                      <span style={{ flex: 1, fontSize: 13 }}>{issueLabel(t.tag)}</span>
                      <span className="mono text-xs" style={{ color: trendColor(t.tag, t.previous, t.current), textAlign: 'right' }}>
                        {t.previous} → {t.current}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div className="card animate-in" style={{ animationDelay: '0.2s' }}>
              <div className="card-label">ANNOTATOR LEADERBOARD · 30 DAYS</div>
              {(overview?.leaderboard || []).length === 0 ? (
                <div className="text-muted text-xs">Nobody has annotated a call or added a pattern in the last 30 days.</div>
              ) : overview.leaderboard.map((r, i) => (
                <div key={r.author} className="flex-row" style={{ marginBottom: 8 }}>
                  <Placeholder w={26} h={26} round label={String(r.author).slice(0, 2).toUpperCase()} />
                  <span style={{ flex: 1, fontSize: 13 }}>{r.author}</span>
                  {i === 0 && <span className="pill warning filled" style={{ fontSize: 9 }}>★ top</span>}
                  <span className="mono text-xs">{r.annotations} notes · {r.patterns} patterns</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {dialog && <PatternDialog busy={busy} onClose={() => setDialog(false)} onConfirm={onAdd} />}
    </>
  );
}
